import * as css from './AccountSwitcher.css';
import {useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {List} from '@tickchak/react-share/app';
import useShow from '../../../../../hooks/useShow';
import {dispatchCustomEvent} from '../../../../../hooks/dispatchEvent';
import useAccountSwitcher from './useAccountSwitcher';

function AccountSwitcher({cardRef}: {cardRef: React.RefObject<HTMLDivElement>}) {
    const account = useSelector((s: any) => s.account);
    const {t} = useTranslation('package');
    const navigate = useNavigate();
    const show = useShow();
    const [position, setPosition] = useState<any>({width: '375px', maxHeight: '400px'});
    const {accounts, search, fetchAccounts, popupRef, hasMultiple} = useAccountSwitcher(show.isOpen);

    if(!hasMultiple) return null;

    const open = (e) => {
        const card = cardRef.current;
        if(card) {
            const caret = e.currentTarget.getBoundingClientRect();
            const rect = card.getBoundingClientRect();
            setPosition({width: '375px', maxHeight: '400px',
                top: Math.round(caret.bottom - rect.top + 8),
                inlineStart: Math.round(rect.right - caret.right) + 'px'});
        }
        fetchAccounts();
        show.open();
    };

    const switchTo = (pid) => {
        dispatchCustomEvent('changePackage', {detail: pid});
        dispatchCustomEvent('changeEvent', {detail: null});
        navigate({pathname: '/p/' + pid + '/home'});
        show.close();
    };

    const items = accounts.map((a) => ({
        id: a.pid, text: a.title, subText: `${t('accountNumberShort')} ${a.pid}`,
        selected: a.pid === account.pid, selectionType: 'checkbox' as const,
        ...(a.image
            ? {avatar: {src: a.image, alt: a.title, size: 'lg' as const}}
            : {icon: <css.avatarPlaceholder/>}),
    }));

    return (
        <>
            <css.nameCaret onClick={open} data-cy="account-switcher-trigger"/>
            {show.isOpen &&
                <List show={show} position={position} forwardRef={popupRef} data-cy="account-switcher"
                    onItemClick={(it) => switchTo(Number(it.id))}>
                    <List.Header title={t('switchAccount')}/>
                    <List.Search placeholder={t('searchAccount')} onSearch={fetchAccounts} searchDelayMs={500} autoFocus/>
                    <List.Items items={items}/>
                    {!!search && !items.length &&
                        <css.empty data-cy="account-switcher-empty">{t('noResults')}</css.empty>}
                </List>}
        </>
    );
}

export default AccountSwitcher;
